// src/components/Dashboard/ClaimCases.jsx
// "Casos y notas" DEL RECLAMO — montado en ClaimDetailPage.jsx. Pedido
// explícito 2026-09-21: antes "Casos y notas" solo se creaba desde el panel
// admin, buscando el reclamo a mano; ahora vive donde debería — el
// ajustador crea el caso/nota ACÁ MISMO, mientras mira el reclamo (ej. "este
// reclamo está atascado por firma"), y el admin lo resuelve, sea desde acá
// o desde la vista general de Casos y notas (TeamWorkspace.jsx) — ambas
// leen/escriben la misma colección `notifications` (kind: 'ticket').
import React, { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { LanguageContext } from '../../App';
import { createTicket, updateTicket, getTicketsByClaimRecordId, TICKET_STATES, TICKET_PRIORITIES } from '../../services/workspaceService';
import { stateLabel, priorityLabel } from './TeamWorkspace';
import ClaimNotes from './ClaimNotes';
import './ClaimCases.css';

const formatDateTime = (value) => {
  const ms = value?.toMillis?.() ?? (value ? new Date(value).getTime() : null);
  if (!ms) return '';
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const emptyForm = { title: '', text: '', type: 'case', priority: 'medium' };

const ClaimCases = ({ claimRecordId, canWrite = false, isAdmin = false, claimMeta = {} }) => {
  const { language } = useContext(LanguageContext);
  const es = language === 'es';
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [view, setView] = useState('cases');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    if (!claimRecordId) return;
    setLoading(true);
    try {
      setCases(await getTicketsByClaimRecordId(claimRecordId));
    } catch (err) {
      console.error('Error loading claim cases:', err);
      toast.error(es ? 'No se pudieron cargar los casos del reclamo.' : 'Could not load the claim cases.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimRecordId]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.text.trim()) return;
    setSaving(true);
    try {
      const reference = claimMeta.claimNumber ? `#${claimMeta.claimNumber}${claimMeta.clientName ? ` · ${claimMeta.clientName}` : ''}` : '';
      await createTicket({ ...form, reference, claimRecordId });
      setForm(emptyForm);
      setShowForm(false);
      toast.success(es ? 'Caso creado.' : 'Case created.');
      load();
    } catch (err) {
      console.error('Error creating claim case:', err);
      toast.error(es ? 'No se pudo crear el caso.' : 'Could not create the case.');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (ticketId, status) => {
    setBusyId(ticketId);
    try {
      await updateTicket(ticketId, { status });
      await load();
    } catch (err) {
      console.error('Error updating claim case:', err);
      toast.error(es ? 'No se pudo actualizar el estado.' : 'Could not update the status.');
    } finally {
      setBusyId(null);
    }
  };

  if (!claimRecordId) return null;

  return (
    <section className="cd-attach-section claim-cases">
      <div className="cd-detail-checks-header">
        <h2 className="cd-detail-checks-title">{es ? 'Casos y notas' : 'Cases and notes'}</h2>
        {canWrite && (
          <button type="button" className="cd-attach-toolbar-btn cd-attach-toolbar-btn-wide claim-create-button" aria-expanded={view === 'cases' ? showForm : showNoteForm} onClick={() => view === 'cases' ? setShowForm(v => !v) : setShowNoteForm(v => !v)}>
            <i className="fa-solid fa-plus"></i> <span>{view === 'cases' ? (es ? 'Nuevo caso' : 'New case') : (es ? 'Agregar nota' : 'Add note')}</span>
          </button>
        )}
      </div>

      <div className="claim-cases-tabs" role="group" aria-label={es ? 'Casos y notas' : 'Cases and notes'}>
        <button type="button" aria-pressed={view === 'cases'} onClick={() => setView('cases')}>{es ? 'Casos y seguimiento' : 'Cases and follow-up'} <span>{cases.length}</span></button>
        <button type="button" aria-pressed={view === 'notes'} onClick={() => setView('notes')}>{es ? 'Notas del reclamo' : 'Claim notes'}</button>
      </div>
      {view === 'notes' ? <ClaimNotes embedded showComposer={showNoteForm} onComposerClose={() => setShowNoteForm(false)} claimRecordId={claimRecordId} canWrite={canWrite} claimMeta={claimMeta} /> : <>
      <p className="claim-cases-intro">{es ? 'Consulta el estado de tus solicitudes y las últimas respuestas del equipo.' : 'Track your requests and the latest replies from the team.'}</p>
      {showForm && (
        <form className="claim-case-form" onSubmit={handleCreate}>
          <label>{es ? 'Asunto' : 'Subject'}<input
            required maxLength={120}
            placeholder={es ? 'Título (ej. Reclamo atascado por firma)' : 'Title (e.g. Claim stuck on signature)'}
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
          /></label>
          <label>{es ? 'Solicitud y datos necesarios' : 'Request and required details'}<textarea
            required maxLength={6000} rows={3}
            className="cd-notes-textarea"
            placeholder={es ? 'Describe qué necesitás que resuelva el admin...' : "Describe what you need the admin to resolve..."}
            value={form.text}
            onChange={e => setForm({ ...form, text: e.target.value })}
          /></label>
          <label>
            {es ? 'Prioridad' : 'Priority'}
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={{ display: 'block', width: '100%', marginTop: '.3rem' }}>
              {TICKET_PRIORITIES.map(p => <option key={p} value={p}>{priorityLabel(p, es)}</option>)}
            </select>
          </label>
          <div className="cd-note-edit-actions">
            <button type="submit" className="cd-btn cd-btn-check" disabled={saving || !form.title.trim() || !form.text.trim()}>
              {saving ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Crear' : 'Create')}
            </button>
            <button type="button" disabled={saving} className="cd-attach-mini-btn" onClick={() => { setShowForm(false); setForm(emptyForm); }}>
              {es ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </form>
      )}

      <div className="cd-notes-list">
        {loading && <p className="cd-attach-empty">{es ? 'Cargando casos...' : 'Loading cases...'}</p>}
        {!loading && cases.length === 0 && (
          <p className="cd-attach-empty">{es ? 'Todavía no hay casos ni notas para este reclamo.' : 'No cases or notes for this claim yet.'}</p>
        )}
        {!loading && cases.map((item, index) => (
          <details key={item.id} className="claim-case" open={index === 0}>
            <summary className="claim-case-summary">
              <span className="claim-case-subject"><strong>{item.title}</strong><small>{item.createdBy} · {formatDateTime(item.createdAt)}</small></span>
              <span className={`claim-case-status ${item.status}`}>{stateLabel(item.status, es)}</span>
              <i className="fa-solid fa-chevron-down" aria-hidden="true" />
            </summary>
            <div className="claim-case-content">
              <div className="claim-case-timeline">
                {[...(item.history || [])].reverse().map((entry, entryIndex) => (
                  <article className={`claim-case-message ${entry.text ? 'claim-case-reply' : ''}`} key={entryIndex}>
                    <div className="claim-case-message-meta"><strong>{entry.createdBy}</strong><time>{formatDateTime(entry.createdAt)}</time></div>
                    {entry.text && <p>{entry.text}</p>}
                    {entry.to && <span className={`claim-case-status ${entry.to}`}>{stateLabel(entry.to, es)}</span>}
                    {entry.priorityTo && <p>{es ? 'Prioridad' : 'Priority'}: {priorityLabel(entry.priorityTo, es)}</p>}
                  </article>
                ))}
                <article className="claim-case-message claim-case-original">
                  <div className="claim-case-message-meta"><strong>{es ? 'Solicitud original' : 'Original request'}</strong><time>{formatDateTime(item.createdAt)}</time></div>
                  <p>{item.text}</p>
                  <small>{item.createdBy} · {es ? 'Prioridad' : 'Priority'}: {priorityLabel(item.priority || 'medium', es)}</small>
                </article>
              </div>
              {isAdmin && (
                <label className="claim-case-admin-status">{es ? 'Estado del caso' : 'Case status'}
                  <select value={item.status} disabled={busyId === item.id} onChange={e => handleStatusChange(item.id, e.target.value)}>
                    {TICKET_STATES.map(state => <option key={state} value={state}>{stateLabel(state, es)}</option>)}
                  </select>
                </label>
              )}
            </div>
          </details>
        ))}
      </div>
      </>}
    </section>
  );
};

export default ClaimCases;
